import React, {useCallback, useEffect, useState} from "react";
import useApiStable from "../../utils/useApiStable";
import MatchCostSplitEditor from "./MatchCostSplitEditor";
import "../../styles/LeagueCostSplit.css";

const formatDate = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleDateString("de-DE", {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
};

const fmtEur = (cents) =>
    (Number(cents || 0) / 100).toLocaleString("de-DE", {style: "currency", currency: "EUR"});

const CostBadge = ({summary}) => {
    if (summary && summary.expenseCount > 0) {
        if (summary.balanced) {
            return <span className="lcs-badge lcs-badge--done">✓ bezahlt</span>;
        }
        return (
            <span className="lcs-badge lcs-badge--open">
                offen · {fmtEur(summary.totalCents)}
            </span>
        );
    }
    return <span className="lcs-badge lcs-badge--empty">keine Abrechnung</span>;
};

const CostMatchCard = ({teamId, match, players, summary, onSummaryChange}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="lcs-card">
            <button
                type="button"
                className="lcs-card__head"
                onClick={() => setExpanded(v => !v)}
                aria-expanded={expanded}
            >
                <span className="lcs-card__title">
                    <span className="lcs-card__match">{match.homeTeam} vs. {match.guestTeam}</span>
                    <span className="lcs-card__date">{formatDate(match.matchDateTime)}</span>
                </span>
                <span className="lcs-card__right">
                    <CostBadge summary={summary}/>
                    <span className="lcs-card__chevron">{expanded ? "▲" : "▼"}</span>
                </span>
            </button>

            {expanded && (
                <div className="lcs-card__body">
                    <MatchCostSplitEditor
                        teamId={teamId}
                        matchId={match.matchId}
                        players={players}
                        onSummaryChange={onSummaryChange}
                    />
                </div>
            )}
        </div>
    );
};

const LeagueCostSplitTab = ({teamId, matches, team}) => {
    const api = useApiStable();
    const players = team?.players || [];
    const [summaries, setSummaries] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        api.get(`/league/teams/${teamId}/cost-split-summary`)
            .then(list => {
                if (!active) return;
                const map = {};
                (list || []).forEach(s => {
                    map[s.matchId] = s;
                });
                setSummaries(map);
            })
            .catch(err => {
                if (active) setError(err.message);
            });
        return () => {
            active = false;
        };
    }, [teamId, api]);

    const handleSummaryChange = useCallback((summary) => {
        setSummaries(prev => ({...prev, [summary.matchId]: summary}));
    }, []);

    if (!matches || matches.length === 0) {
        return (
            <p style={{color: "#666", fontStyle: "italic"}}>
                Keine Spieltermine für diese Mannschaft.
            </p>
        );
    }

    const sortedMatches = [...matches]
        .sort((a, b) => new Date(b.matchDateTime) - new Date(a.matchDateTime));

    return (
        <div>
            <p className="lcs-tab__intro">
                Kosten je Spiel erfassen und gleichmäßig auf die Teilnehmer aufteilen. Vollständig
                ausgeglichene Spiele sind grün als „bezahlt" markiert.
            </p>

            {error && <p className="lcs-error">{error}</p>}

            {sortedMatches.map(m => (
                <CostMatchCard
                    key={m.matchId}
                    teamId={teamId}
                    match={m}
                    players={players}
                    summary={summaries[m.matchId]}
                    onSummaryChange={handleSummaryChange}
                />
            ))}
        </div>
    );
};

export default LeagueCostSplitTab;
